import { type ReactNode, useId, useMemo, useState } from 'react';
import { Button } from './Button';
import { ControlBar } from './Chrome';
import { Field, Select, TextInput } from './Field';

export type TableSortDirection = 'ascending' | 'descending';

export interface TableSortState {
  columnId: string;
  direction: TableSortDirection;
}

type TableScalar = string | number | boolean | null | undefined;
type TableValue = TableScalar | readonly TableScalar[];

export interface TableColumn<Row> {
  id: string;
  label: string;
  /** Searchable text matching what the cells present to a person. */
  value: (row: Row) => TableValue;
  /** Render a compact exact-match dropdown in addition to the table-wide search box. */
  filter?: 'select';
  /** A narrower categorical value when `value` contains supporting cell text too. */
  filterValue?: (row: Row) => TableValue;
  /** Machine value used for ordering formatted dates, durations and other display strings. */
  sortValue?: (row: Row) => TableValue;
  /** Domain ordering, such as XS-S-M-L rather than alphabetic size ordering. */
  compare?: (left: TableValue, right: TableValue) => number;
}

export interface TableFilterOption {
  value: string;
  label: string;
}

interface ActiveTableColumn {
  id: string;
  label: string;
  options: TableFilterOption[];
}

export interface TableController<Row> {
  rows: readonly Row[];
  rowCount: number;
  totalCount: number;
  query: string;
  setQuery: (query: string) => void;
  filters: Readonly<Record<string, string>>;
  filterColumns: readonly ActiveTableColumn[];
  setFilter: (columnId: string, value: string) => void;
  clearFilters: () => void;
  hasFilters: boolean;
  sort: TableSortState | null;
  cycleSort: (columnId: string) => void;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function scalars(value: TableValue): readonly TableScalar[] {
  return Array.isArray(value) ? value : [value as TableScalar];
}

function displayValue(value: TableScalar): string {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function searchableValue(value: TableValue): string {
  return scalars(value).map(displayValue).join(' ').toLocaleLowerCase();
}

function sortValue(value: TableValue): TableScalar {
  if (!Array.isArray(value)) return value as TableScalar;
  return value.map(displayValue).join(', ');
}

function compareValues(left: TableValue, right: TableValue): number {
  const a = sortValue(left);
  const b = sortValue(right);
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}

/**
 * One state model for every table in the portal.
 *
 * Search checks every declared data column. Select filters are additive, and sorting is stable:
 * clearing the header sort restores the order supplied by the server rather than inventing a
 * new default. Components keep rendering their own cells, so an editable/admin row does not
 * have to squeeze its controls through a generic renderer.
 */
export function useTableController<Row>(
  sourceRows: readonly Row[],
  columns: readonly TableColumn<Row>[],
): TableController<Row> {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<TableSortState | null>(null);

  const filterColumns = useMemo<ActiveTableColumn[]>(
    () =>
      columns
        .filter((column) => column.filter === 'select')
        .map((column) => {
          const labels = new Map<string, string>();
          for (const row of sourceRows) {
            for (const value of scalars((column.filterValue ?? column.value)(row))) {
              const label = displayValue(value);
              const normalized = label.toLocaleLowerCase();
              if (!labels.has(normalized)) labels.set(normalized, label);
            }
          }
          return {
            id: column.id,
            label: column.label,
            options: Array.from(labels, ([value, label]) => ({ value, label })).sort((a, b) =>
              collator.compare(a.label, b.label),
            ),
          };
        }),
    [columns, sourceRows],
  );

  const rows = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const activeFilters = Object.entries(filters).filter(([, value]) => value !== '');
    const filtered = sourceRows.filter((row) => {
      if (
        normalizedQuery !== '' &&
        !columns.some((column) => searchableValue(column.value(row)).includes(normalizedQuery))
      ) {
        return false;
      }
      return activeFilters.every(([columnId, expected]) => {
        const column = columns.find((candidate) => candidate.id === columnId);
        return (
          column !== undefined &&
          scalars((column.filterValue ?? column.value)(row)).some(
            (value) => displayValue(value).toLocaleLowerCase() === expected,
          )
        );
      });
    });

    if (sort === null) return filtered;
    const column = columns.find((candidate) => candidate.id === sort.columnId);
    if (column === undefined) return filtered;
    return filtered
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const compared = (column.compare ?? compareValues)(
          (column.sortValue ?? column.value)(left.row),
          (column.sortValue ?? column.value)(right.row),
        );
        const directed = sort.direction === 'ascending' ? compared : -compared;
        return directed === 0 ? left.index - right.index : directed;
      })
      .map(({ row }) => row);
  }, [columns, filters, query, sort, sourceRows]);

  return {
    rows,
    rowCount: rows.length,
    totalCount: sourceRows.length,
    query,
    setQuery,
    filters,
    filterColumns,
    setFilter: (columnId, value) =>
      setFilters((current) => {
        if (value === '') {
          const { [columnId]: _removed, ...rest } = current;
          return rest;
        }
        return { ...current, [columnId]: value };
      }),
    clearFilters: () => {
      setQuery('');
      setFilters({});
    },
    hasFilters: query.trim() !== '' || Object.values(filters).some((value) => value !== ''),
    sort,
    cycleSort: (columnId) =>
      setSort((current) => {
        if (current?.columnId !== columnId) return { columnId, direction: 'ascending' };
        if (current.direction === 'ascending') return { columnId, direction: 'descending' };
        return null;
      }),
  };
}

export function TableToolbar<Row>({
  label,
  controller,
  page = false,
  showSearch = true,
}: {
  label: string;
  controller: TableController<Row>;
  /** The source array is one server-paginated page, rather than the whole result set. */
  page?: boolean;
  /** False when the screen already owns a server-wide search above this page-local toolbar. */
  showSearch?: boolean;
}) {
  const generatedId = useId();
  const idBase = `table-${generatedId.replaceAll(':', '')}`;
  const scope = page ? ' on this page' : '';

  return (
    <fieldset className="table-toolbar">
      <legend className="vh">
        Filter {label}
        {scope}
      </legend>
      <ControlBar>
        {showSearch ? (
          <Field id={`${idBase}-search`} label={page ? 'Search this page' : 'Search'}>
            <TextInput
              id={`${idBase}-search`}
              aria-label={`Search ${label}${scope}`}
              type="search"
              grow
              placeholder={`Search ${label}${scope}…`}
              value={controller.query}
              onChange={(event) => controller.setQuery(event.target.value)}
            />
          </Field>
        ) : null}
        {controller.filterColumns.map((column) => (
          <Field key={column.id} id={`${idBase}-${column.id}`} label={column.label}>
            <Select
              id={`${idBase}-${column.id}`}
              aria-label={`Filter ${label} by ${column.label}`}
              value={controller.filters[column.id] ?? ''}
              onChange={(event) => controller.setFilter(column.id, event.target.value)}
            >
              <option value="">Any {column.label.toLocaleLowerCase()}</option>
              {column.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        ))}
        <Button
          tone="ghost"
          size="sm"
          disabled={!controller.hasFilters}
          onClick={controller.clearFilters}
        >
          Clear filters
        </Button>
        <span className="table-filter-status" role="status" aria-live="polite">
          Showing {controller.rowCount} of {controller.totalCount} rows{scope}
        </span>
      </ControlBar>
    </fieldset>
  );
}

export function SortableTableHeader({
  columnId,
  label,
  sort,
  onSort,
  numeric = false,
  align,
}: {
  columnId: string;
  label: ReactNode;
  sort: TableSortState | null;
  onSort: (columnId: string) => void;
  numeric?: boolean | undefined;
  align?: 'left' | 'center' | 'right' | undefined;
}) {
  const direction = sort?.columnId === columnId ? sort.direction : undefined;
  const next =
    direction === undefined
      ? 'ascending'
      : direction === 'ascending'
        ? 'descending'
        : 'the original order';

  return (
    <th
      scope="col"
      aria-sort={direction ?? 'none'}
      className={`table-sort-header${numeric ? ' n' : ''}`}
      style={align === undefined ? undefined : { textAlign: align }}
    >
      <button
        type="button"
        className="table-sort-button"
        aria-label={`Sort by ${typeof label === 'string' ? label : columnId}, ${next}`}
        onClick={() => onSort(columnId)}
      >
        <span>{label}</span>
        <span className="table-sort-mark" aria-hidden="true">
          {direction === 'ascending' ? '↑' : direction === 'descending' ? '↓' : '↕'}
        </span>
      </button>
    </th>
  );
}
